/**
 * GMA-1624 (CompassCare Web — SITRep Board) — route container.
 *
 * Mounts the app-shell (WebShell) around the SITRep board (SitrepPage) at
 * /web/intel/sitrep. Placement: the board has no slot in the frozen
 * 13-section nav (webNavModel contract), so it nests under INTEL —
 * findNavSectionByPath resolves the nested path to the intel section and the
 * sidebar highlights correctly with zero nav-model changes. Reached from
 * IntelPage's "Generate Intel SITRep" (which passes ?watch=<id> provenance)
 * and Overview's "Generate SITRep" quick action.
 *
 * The live report feed is Nora backend work (the GMA-1624 follow-up), so the
 * page renders the demo board in demo builds and its calm empty state
 * otherwise, inside a real, navigable frame. The ?watch= param resolves
 * against the demo watches to a provenance line + the watch's first impacted
 * client; unknown ids are ignored (never a throw).
 */
import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import WebShell from "../../components/web/WebShell";
import SitrepPage from "./SitrepPage";
import { resolveWatchImpact } from "./IntelPage";
import {
  DEMO_DATA_ENABLED,
  getDemoClients,
  getDemoSitrep,
  getDemoWatches,
} from "./demoData";

export default function WebSitrepRoute() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Capture one timestamp so demo generation stays deterministic for the
  // render (matches the other /web route containers).
  const now = Date.now();
  const sitrep = DEMO_DATA_ENABLED ? getDemoSitrep(now) : null;

  const watchId = searchParams.get("watch");
  let provenance = null;
  let initialClientId = null;
  if (DEMO_DATA_ENABLED && watchId) {
    const watch = getDemoWatches().find((w) => w.id === watchId) || null;
    if (watch) {
      provenance = { watchId: watch.id, watchTitle: watch.title };
      const impacted = resolveWatchImpact(watch, getDemoClients(now));
      initialClientId = impacted.length ? impacted[0].id : null;
    }
  }

  return (
    <WebShell
      activeKey="intel"
      onNavigate={(section) => {
        if (section && section.path) navigate(section.path);
      }}
      title="SITRep Board"
    >
      <SitrepPage
        sitrep={sitrep}
        initialClientId={initialClientId}
        provenance={provenance}
      />
    </WebShell>
  );
}
